/**

Coded by www.techhighway.ca

*/

import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 React components
import customAlert from "components/MDAlert/customAlert";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDCircularProgress from "components/MDCircularProgress";
import MDInput from "components/MDInput";
import MDSwitch from "components/MDSwitch";

// Material Dashboard 2 React example components
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import StatusesService from "services/status-service";

// @mui icons
import { useCache } from "context";

function Form() {
  const { action } = useParams();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const navigate = useNavigate();
  const location = useLocation();

  const { openAlertSB, closeAlertSB, renderAlertSB } = customAlert();
  const [active, setActive] = useState(1);
  const [headerTitle, setHeaderTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdBy, setCreatedBy] = useState("");
  const { cacheItems, setCacheItems } = useCache();
  const [isData, setIsData] = useState(false);

  const [inputs, setInputs] = useState({
    name: "",
    created_at: "",
  });

  const [errors, setErrors] = useState({
    nameError: false,
  });

  useEffect(() => {
    if (action === "add" && id == null) {
      setIsData(true);
      return;
    }

    const showStatus = async () => {
      try {
        const data = await StatusesService.showStatus(id);
        setHeaderTitle(data?.name);
        setInputs(data);
        setCreatedBy(
          `${data?.user?.first_name + (data?.user?.middle_name ? ` ${data?.user?.middle_name}` : "")} ${
            data?.user?.last_name
          }`
        );
        setActive(data?.active);
        setIsData(true);
      } catch (res) {
        setIsData(true);
        if (res.hasOwnProperty("message")) {
          const state = { color: "error", icon: "warning", msg: res.message, title: "Status" };
          navigate("/manage-statuses", { state });
        } else {
          const state = {
            color: "error",
            icon: "warning",
            msg: res.errors[0].detail,
            title: "Status",
          };
          navigate("/manage-statuses", { state });
        }
      }
    };
    showStatus();
  }, []);

  const changeHandler = (e) => {
    setLoading(false);
    setInputs({
      ...inputs,
      [e.target.name]: e.target.value,
    });
  };

  const submitHandler = async (e) => {
    setLoading(true);
    e.preventDefault();
    if (inputs.name.trim().length < 1) {
      setErrors({ ...errors, nameError: true });
      return;
    }

    const newStatus = { name: inputs.name, active: active ? 1 : 0 };

    if (action === "add") {
      try {
        const response = await StatusesService.addStatus(newStatus);
        setLoading(false);
        if (response) {
          if (cacheItems.hasOwnProperty("statuses")) {
            // Create a new object without the key
            const { statuses, ...rest } = cacheItems; // Destructure to exclude key
            setCacheItems(rest); // Set the new cache items without key
          }
          const state = {
            color: "success",
            icon: "check",
            msg: "Status Added Successfully!",
            title: "Status",
          };
          navigate("/manage-statuses", { state });
        }
      } catch (res) {
        if (res.hasOwnProperty("message")) {
          openAlertSB({ color: "error", icon: "warning", msg: res.message, title: "Status" });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        } else {
          openAlertSB({
            color: "error",
            icon: "warning",
            msg: res.errors[0].detail,
            title: "Status",
          });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        }
        setLoading(false);
      }
    } else {
      try {
        const response = await StatusesService.updateStatus(id, newStatus);
        setLoading(false);
        if (response) {
          if (cacheItems.hasOwnProperty("statuses")) {
            // Create a new object without the key
            const { statuses, ...rest } = cacheItems; // Destructure to exclude key
            setCacheItems(rest); // Set the new cache items without key
          }
          openAlertSB({
            color: "success",
            icon: "check",
            msg: "Status Updated Successfully!",
            title: "Status",
          });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        }
      } catch (res) {
        if (res.hasOwnProperty("message")) {
          openAlertSB({ color: "error", icon: "warning", msg: res.message, title: "Status" });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        } else {
          openAlertSB({
            color: "error",
            icon: "warning",
            msg: res.errors[0].detail,
            title: "Status",
          });
          setTimeout(function () {
            closeAlertSB();
          }, 4000);
        }
        setLoading(false);
      }
    }

    return () => {
      setInputs({
        name: "",
      });

      setErrors({
        nameError: false,
      });
    };
  };

  return (
    <>
      <DashboardLayout
        breadcrumbsTitle={action === "add" ? "Add Status" : `Edit Status : ${headerTitle}`}
        isNotFound={inputs?.id && inputs?.id > 0 || action === 'add' ? false : true}
        isData={isData}
      >
        <MDBox py={3}>
          <MDBox component="form" role="form" method="POST" onSubmit={submitHandler}>
            <Grid container>
              <Grid item xs={12} md={6} px={3}>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Name"
                    fullWidth
                    value={inputs.name}
                    name="name"
                    inputProps={{ maxLength: 50 }}
                    onChange={changeHandler}
                    error={errors.nameError}
                    required
                  />
                </MDBox>
              </Grid>

              {action === "edit" && (
                <>
                  <Grid item md={6} xs={12} px={3}>
                    <MDBox mb={2}>
                      <MDInput
                        type="text"
                        label="Created By"
                        fullWidth
                        name=""
                        value={createdBy}
                        inputProps={{ maxLength: 50 }}
                        readOnly
                      />
                    </MDBox>
                  </Grid>
                  <Grid item md={6} xs={12} px={3}>
                    <MDBox mb={2}>
                      <MDInput
                        type="text"
                        label="Created At"
                        fullWidth
                        value={dayjs(inputs.created_at).format("DD-MM-YYYY HH:mm A")}
                        inputProps={{ maxLength: 50 }}
                        readOnly
                      />
                    </MDBox>
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={2} px={3}>
                <MDBox mb={2}>
                  <MDSwitch
                    checked={!!active}
                    onChange={() => setActive((prev) => !prev)}
                    label="Active"
                    inputProps={{ "aria-label": "controlled" }}
                    color="info"
                  />
                </MDBox>
              </Grid>
              <Grid item md={10} xs={8} />
              <Grid item md={1} xs={2}>
                <MDBox mr={2}>
                  <MDButton
                    component={Link}
                    to="/manage-statuses"
                    variant="gradient"
                    color="primary"
                    fullWidth
                  >
                    Back
                  </MDButton>
                </MDBox>
              </Grid>
              <Grid item md={1} xs={2}>
                <MDBox mr={3}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    fullWidth
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <MDCircularProgress
                        size={24}
                        sx={{
                          position: "absolute",
                        }}
                      />
                    ) : action === "add" ? (
                      "Save"
                    ) : (
                      "Update"
                    )}
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </MDBox>
      </DashboardLayout>
      {renderAlertSB}
    </>
  );
}

export default Form;
